import { apiRequest } from '@/lib/api-client';

export type IntegrationProvider = 'meta' | 'tiktok' | 'linkedin' | 'pinterest' | 'youtube';

export interface IntegrationStatus {
  provider: IntegrationProvider;
  configured: boolean;
  connected: boolean;
  accountName: string | null;
  connectedAt: string | null;
  /** Meta: the Page this project publishes to, once picked. */
  pageId: string | null;
  pageName: string | null;
  instagramLinked: boolean;
  adAccountId: string | null;
  adAccountName: string | null;
}

export interface MetaPage {
  id: string;
  name: string;
  instagramLinked: boolean;
}

export interface MetaAdAccount {
  id: string;
  name: string;
  currency: string;
  amountSpent: number;
}

export interface SelectedAdAccount {
  adAccountId: string;
  adAccountName: string;
}

export interface SelectedPage {
  pageId: string;
  pageName: string;
  instagramLinked: boolean;
}

export interface RecentPost {
  id: string;
  caption: string;
  url: string | null;
  createdAt: string | null;
}

export interface FacebookInsights {
  pageName: string;
  followers: number;
  postCount: number | null;
  recentPosts: RecentPost[];
  impressions28d: number | null;
  engagements28d: number | null;
}

export interface InstagramInsights {
  username: string;
  followers: number;
  mediaCount: number;
  recentMedia: RecentPost[];
}

export interface AdCampaignSummary {
  id: string;
  name: string;
  status: string;
  effectiveStatus: string;
}

export interface AdsInsights {
  currency: string;
  adAccountId: string;
  lifetimeSpend: number;
  spend30d: number;
  activeCampaigns: number;
  campaigns: AdCampaignSummary[];
}

export interface MetaInsights {
  pageSelected: boolean;
  facebook: FacebookInsights | null;
  instagram: InstagramInsights | null;
  ads: AdsInsights | null;
}

const base = (orgId: string, projectId: string) =>
  `/api/v1/organizations/${orgId}/projects/${projectId}/integrations`;

export const integrationsApi = {
  list: (orgId: string, projectId: string) =>
    apiRequest<IntegrationStatus[]>(base(orgId, projectId)),

  connect: (orgId: string, projectId: string, provider: IntegrationProvider) =>
    apiRequest<{ authUrl: string }>(`${base(orgId, projectId)}/${provider}/connect`, {
      method: 'POST',
    }),

  disconnect: (orgId: string, projectId: string, provider: IntegrationProvider) =>
    apiRequest<{ disconnected: boolean }>(`${base(orgId, projectId)}/${provider}`, {
      method: 'DELETE',
    }),

  listMetaPages: (orgId: string, projectId: string) =>
    apiRequest<MetaPage[]>(`${base(orgId, projectId)}/meta/pages`),

  selectMetaPage: (orgId: string, projectId: string, pageId: string) =>
    apiRequest<SelectedPage>(`${base(orgId, projectId)}/meta/page`, {
      method: 'POST',
      body: { pageId },
    }),

  listMetaAdAccounts: (orgId: string, projectId: string) =>
    apiRequest<MetaAdAccount[]>(`${base(orgId, projectId)}/meta/adaccounts`),

  selectMetaAdAccount: (orgId: string, projectId: string, adAccountId: string) =>
    apiRequest<SelectedAdAccount>(`${base(orgId, projectId)}/meta/adaccount`, {
      method: 'POST',
      body: { adAccountId },
    }),

  metaInsights: (orgId: string, projectId: string) =>
    apiRequest<MetaInsights>(`${base(orgId, projectId)}/meta/insights`),
};
